import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import PasswordInput from 'components/field/PasswordInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useChangeLanguage from 'hooks/useChangeLanguage';
import useLocale from 'hooks/useLocale';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { languages } from 'utils/localization';
import PATHS from 'utils/paths';

import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Common.styled';

export default function ResetPassword() {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					password: yup
						.string()
						.required()
						.matches(/^(?=.{8,})/, t('passwordError.8Character'))
						.matches(/^(?=.*[a-z])/, t('passwordError.lowerCase'))
						.matches(/^(?=.*[A-Z])/, t('passwordError.upperCase'))
						.matches(/^(?=.*\d)/, t('passwordError.oneNumber')),
					passwordAgain: yup
						.string()
						.required()
						.oneOf([yup.ref('password'), null], t('passwordError.mustMatch'))
				})
				.required(),
		[]
	);

	const { LangCountryCode } = useLocale();

	const [loading, setLoading] = useState(false);
	const { state } = useLocation();
	const navigate = useNavigate();
	const { showSuccess, showError, showWarning } = useAlert();
	const { lang } = useParams();
	const changeLanguage = useChangeLanguage();
	const [searchParams] = useSearchParams();

	const formMethods = useForm({
		defaultValues: {},
		resolver: yupResolver(schema)
	});
	const { watch, handleSubmit } = formMethods;

	useEffect(() => {
		const language = languages.find((language) => language.systemCode === lang);
		if (language) {
			if (language !== LangCountryCode) changeLanguage(language.code);
		} else {
			navigate(
				{ pathname: `${PATHS.RESET_PASSWORD}/${LangCountryCode}`, search: searchParams.toString() },
				{
					state: { redirectTo: state?.redirectTo }
				}
			);
		}
	}, [lang]);

	const watchedPassword = watch('password');

	const onSubmit = async () => {
		setLoading(true);
		try {
			const token = searchParams.get('token');
			if (token) {
				await openAxios.post(OPEN.UPDATE_PASSWORD, { token, password: watchedPassword });
				showSuccess();
				navigate(`${PATHS.LOGIN}/${LangCountryCode}`);
			}
		} catch (error) {
			if (error.response?.data?.code === 'error.G001') {
				showError({
					header: 'alert.youCannotUseOldPasswordHeader',
					message: 'alert.youCannotUseOldPasswordMessage'
				});
			} else {
				showError();
			}
		}

		setLoading(false);
	};

	useEffect(() => {
		const token = searchParams.get('token');
		if (!token) navigate(`${PATHS.LOGIN}/${LangCountryCode}`);

		const isForced = searchParams.get('force');
		if (isForced) {
			showWarning({
				header: 'alert.forceResetPasswordHeader',
				message: 'alert.forceResetPasswordMessage',
				persist: true
			});
		}
	}, []);

	const password = watch('password');

	const validation = useMemo(
		() => ({
			upperCase: password?.match(/[A-Z]/) ?? false,
			lowerCase: password?.match(/[a-z]/) ?? false,
			number: password?.match(/\d/) ?? false,
			length: password?.length >= 8
		}),
		[password]
	);

	return (
		<S.FormElemContainer>
			<Form formMethods={formMethods} style={{ backgroundColor: 'unset', border: 'unset' }}>
				<FormItem>
					<Typography variant="h2" fontWeight="500" gutterBottom>
						changePassword
					</Typography>
				</FormItem>
				<FormItem label="newPassword" required>
					<PasswordInput name="password" required />
				</FormItem>
				<FormItem label="newPasswordAgain" required>
					<PasswordInput name="passwordAgain" required />
				</FormItem>
				{watchedPassword && (
					<S.SecurityRequirementsWrapper className="security-requirements">
						<S.SecurityRequirementsContainer>
							{['length', 'number', 'upperCase', 'lowerCase'].map((item, index) => (
								<S.SecurityRequirementsItem key={index}>
									<Icon
										icon={faCircleCheck}
										size="medium"
										color={validation[item] ? 'var(--blue)' : 'var(--content-4)'}
									/>
									<Typography>{`passwordRequirements.${item}`}</Typography>
								</S.SecurityRequirementsItem>
							))}
						</S.SecurityRequirementsContainer>
					</S.SecurityRequirementsWrapper>
				)}
				<FormItem>
					<Button
						size="large"
						loading={loading}
						loadingLabel="changePassword"
						style={{ width: '100%' }}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}>
						<Typography className="button-text" fontWeight="500">
							changePassword
						</Typography>
					</Button>
				</FormItem>
			</Form>
		</S.FormElemContainer>
	);
}
